
import { Injectable } from '@angular/core';
import { EditorState } from '@models/editorState/editorState.model';
import { EditorTab } from '@models/editorState/editorTab.model';
import { SidebarState } from '@models/editorState/sidebarState.model';
import { UpdatePlantillaEscritoRequest } from '@models/plantillaEscrito/updatePlantillaEscritoRequest.model';
import { EscritoService } from '@services/escrito/escrito.service';
import { PlantillaEscritoService } from '@services/plantillaEscrito/plantillaEscrito.service';
import { BehaviorSubject, Observable, Subject, takeUntil, timer } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class EditorStateService {

  private readonly MAX_TABS = 5;
  private readonly AUTO_SAVE_INTERVAL = 30000; // 30 segundos

  private editorState$ = new BehaviorSubject<EditorState>({
    tabs: [],
    sidebar: {
      isCollapsed: false,
      activePanel: 'explorador',
      width: 300
    },
    autoSaveEnabled: true,
    autoSaveInterval: this.AUTO_SAVE_INTERVAL
  });

  private autoSaveSubscription$ = new Subject<void>();

  constructor(
    private escritoService: EscritoService,
    private plantillaService: PlantillaEscritoService
  ) {
    this.setupAutoSave();
  }

  getEditorState(): Observable<EditorState> {
    return this.editorState$.asObservable();
  }

  getCurrentState(): EditorState {
    return this.editorState$.value;
  }

  // TABS MANAGEMENT
  openTab(tab: Partial<EditorTab>): void {
    const state = this.getCurrentState();

    // Verificar si ya existe un tab para esta entidad
    const existingTab = state.tabs.find(t =>
      t.tipo === tab.tipo && t.entityId === tab.entityId
    );

    if (existingTab) {
      this.setActiveTab(existingTab.id);
      return;
    }

    // Verificar límite de tabs
    if (state.tabs.length >= this.MAX_TABS) {
      const tabToClose = state.tabs.find(t => !t.hasChanges);
      if (tabToClose) {
        this.closeTab(tabToClose.id);
      } else {
        throw new Error(`Máximo ${this.MAX_TABS} tabs permitidos`);
      }
    }

    const newTab: EditorTab = {
      id: this.generateTabId(),
      tipo: tab.tipo || 'escrito',
      entityId: tab.entityId,
      titulo: tab.titulo || 'Nuevo',
      contenido: tab.contenido || '',
      hasChanges: false,
      isNew: !tab.entityId,
      lastSaved: new Date(),
      entity: tab.entity
    };

    this.updateState({
      tabs: [...state.tabs, newTab],
      activeTabId: newTab.id
    });
  }

  closeTab(tabId: string, force: boolean = false): boolean {
    const state = this.getCurrentState();
    const tab = state.tabs.find(t => t.id === tabId);

    if (!tab) return false;

    if (tab.hasChanges && !force) {
      return false; // Requiere confirmación
    }

    const newTabs = state.tabs.filter(t => t.id !== tabId);
    let newActiveTabId = state.activeTabId;

    if (state.activeTabId === tabId) {
      newActiveTabId = newTabs.length > 0 ? newTabs[newTabs.length - 1].id : undefined;
    }

    this.updateState({
      tabs: newTabs,
      activeTabId: newActiveTabId
    });

    return true;
  }

  setActiveTab(tabId: string): void {
    this.updateState({ activeTabId: tabId });
  }

  updateTabContent(tabId: string, content: string): void {
    const state = this.getCurrentState();
    const tabs = state.tabs.map(tab =>
      tab.id === tabId
        ? { ...tab, contenido: content, hasChanges: true }
        : tab
    );
    this.updateState({ tabs });
  }

  markTabAsSaved(tabId: string): void {
    const state = this.getCurrentState();
    const tabs = state.tabs.map(tab =>
      tab.id === tabId
        ? { ...tab, hasChanges: false, lastSaved: new Date() }
        : tab
    );
    this.updateState({ tabs });
  }

  getActiveTab(): EditorTab | undefined {
    const state = this.getCurrentState();
    return state.tabs.find(t => t.id === state.activeTabId);
  }

  // SIDEBAR MANAGEMENT
  toggleSidebar(): void {
    const state = this.getCurrentState();
    this.updateState({
      sidebar: {
        ...state.sidebar,
        isCollapsed: !state.sidebar.isCollapsed
      }
    });
  }

  setSidebarPanel(panel: SidebarState['activePanel']): void {
    const state = this.getCurrentState();
    this.updateState({
      sidebar: {
        ...state.sidebar,
        activePanel: state.sidebar.activePanel === panel ? null : panel,
        isCollapsed: false
      }
    });
  }

  // AUTO SAVE
  private setupAutoSave(): void {
    timer(this.AUTO_SAVE_INTERVAL, this.AUTO_SAVE_INTERVAL)
      .pipe(takeUntil(this.autoSaveSubscription$))
      .subscribe(() => {
        const state = this.getCurrentState();
        if (state.autoSaveEnabled && state.activeTabId) {
          const activeTab = state.tabs.find(t => t.id === state.activeTabId);
          if (activeTab?.hasChanges && !activeTab.isNew && activeTab.entityId) {
            this.autoSaveTab(activeTab);
          }
        }
      });
  }

  private autoSaveTab(tab: EditorTab): void {
    if (tab.tipo === 'escrito' && tab.entityId) {
      this.escritoService.update({
        id: tab.entityId,
        titulo: tab.titulo,
        cuerpoHTML: tab.contenido,
        descripcion: tab.entity?.descripcion ?? '',
        idEstadoEscrito: (tab.entity && 'idEstadoEscrito' in tab.entity) ? (tab.entity as any).idEstadoEscrito ?? 0 : 0,
        idExpediente: (tab.tipo === 'escrito' && tab.entity && 'idExpediente' in tab.entity) ? (tab.entity as any).idExpediente ?? 0 : 0,
        fechaModificacion: new Date().toISOString(),
        idTipoEscrito: typeof tab.entity?.tipoEscrito === 'number'
          ? tab.entity.tipoEscrito
          : (typeof tab.entity?.tipoEscrito === 'object' && tab.entity?.tipoEscrito?.id
              ? tab.entity.tipoEscrito.id
              : 0)
      }).subscribe({
        next: () => {
          console.log(`Auto-guardado: ${tab.titulo}`);
          this.markTabAsSaved(tab.id);
        },
        error: (error) => {
          console.error('Error en auto-guardado:', error);
        }
      });
    } else if (tab.tipo === 'plantilla' && tab.entityId) {
      this.plantillaService.update({
        id: tab.entityId,
        titulo: tab.titulo,
        cuerpoHTML: tab.contenido,
        fechaAlta: ('fechaAlta' in (tab.entity ?? {})) ? (tab.entity as any).fechaAlta ?? new Date().toISOString() : new Date().toISOString(),
        idUsuario: ('idUsuario' in (tab.entity ?? {})) ? (tab.entity as any).idUsuario ?? 0 : 0,
        idTipoEscrito: tab.entity?.tipoEscrito ?? null
      } as UpdatePlantillaEscritoRequest).subscribe({
        next: () => {
          console.log(`Auto-guardado: ${tab.titulo}`);
          this.markTabAsSaved(tab.id);
        },
        error: (error) => {
          console.error('Error en auto-guardado:', error);
        }
      });
    }
  }

  saveActiveTab(): Observable<any> {
    const activeTab = this.getActiveTab();
    if (!activeTab) {
      return new Observable(observer => {
        observer.error(new Error('No hay tab activo para guardar'));
      });
    }

    if (activeTab.tipo === 'escrito') {
      if (activeTab.isNew) {
        return this.escritoService.create({
          titulo: activeTab.titulo,
          cuerpoHTML: activeTab.contenido,
          descripcion: activeTab.entity?.descripcion ?? '',
          idEstadoEscrito: (activeTab.entity && 'idEstadoEscrito' in activeTab.entity) ? (activeTab.entity as any).idEstadoEscrito ?? 0 : 0,
          idExpediente: (activeTab.entity && 'idExpediente' in activeTab.entity) ? (activeTab.entity as any).idExpediente ?? 0 : 0,
          idTipoEscrito: typeof activeTab.entity?.tipoEscrito === 'number'
            ? activeTab.entity.tipoEscrito
            : (typeof activeTab.entity?.tipoEscrito === 'object' && activeTab.entity?.tipoEscrito?.id
                ? activeTab.entity.tipoEscrito.id
                : null),
          fechaCreacion: new Date().toISOString()
        });
      } else {
        return this.escritoService.update({
          id: activeTab.entityId ?? 0,
          titulo: activeTab.titulo,
          cuerpoHTML: activeTab.contenido,
          descripcion: activeTab.entity?.descripcion ?? '',
          idEstadoEscrito: (activeTab.entity && 'idEstadoEscrito' in activeTab.entity) ? (activeTab.entity as any).idEstadoEscrito ?? 0 : 0,
          idExpediente: (activeTab.entity && 'idExpediente' in activeTab.entity) ? (activeTab.entity as any).idExpediente ?? 0 : 0,
          fechaModificacion: new Date().toISOString(),
          idTipoEscrito: typeof activeTab.entity?.tipoEscrito === 'number'
            ? activeTab.entity.tipoEscrito
            : (typeof activeTab.entity?.tipoEscrito === 'object' && activeTab.entity?.tipoEscrito?.id
                ? activeTab.entity.tipoEscrito.id
                : 0)
        });
      }
    } else if (activeTab.tipo === 'plantilla') {
      if (activeTab.isNew) {
        return this.plantillaService.create({
          titulo: activeTab.titulo,
          cuerpoHTML: activeTab.contenido,
          fechaAlta: new Date().toISOString(),
          idUsuario: (activeTab.entity && 'idUsuario' in activeTab.entity) ? (activeTab.entity as any).idUsuario ?? 0 : 0,
          idTipoEscrito: typeof activeTab.entity?.tipoEscrito === 'number'
            ? activeTab.entity.tipoEscrito
            : (typeof activeTab.entity?.tipoEscrito === 'object' && activeTab.entity?.tipoEscrito?.id
                ? activeTab.entity.tipoEscrito.id
                : null)
        });
      } else if (activeTab.entityId) {
        return this.plantillaService.update({
          id: activeTab.entityId,
          titulo: activeTab.titulo,
          cuerpoHTML: activeTab.contenido,
          fechaAlta: ('fechaAlta' in (activeTab.entity ?? {})) ? (activeTab.entity as any).fechaAlta ?? new Date().toISOString() : new Date().toISOString(),
          idUsuario: (activeTab.entity && 'idUsuario' in activeTab.entity) ? (activeTab.entity as any).idUsuario ?? 0 : 0,
          idTipoEscrito: typeof activeTab.entity?.tipoEscrito === 'number'
            ? activeTab.entity.tipoEscrito
            : (typeof activeTab.entity?.tipoEscrito === 'object' && activeTab.entity?.tipoEscrito?.id
                ? activeTab.entity.tipoEscrito.id
                : null),
          descripcion: (activeTab.entity && 'descripcion' in activeTab.entity) ? (activeTab.entity as any).descripcion ?? '' : '',
          observaciones: (activeTab.entity && 'observaciones' in activeTab.entity) ? (activeTab.entity as any).observaciones ?? '' : ''
        });
      }
    }

    return new Observable(observer => {
      observer.error(new Error('No se puede guardar el tab'));
    });
  }

  private updateState(partialState: Partial<EditorState>): void {
    const currentState = this.getCurrentState();
    this.editorState$.next({ ...currentState, ...partialState });
  }

  private generateTabId(): string {
    return 'tab_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  destroy(): void {
    this.autoSaveSubscription$.next();
    this.autoSaveSubscription$.complete();
  }

}
